import trackError from '@helpers/trackError'
import { getCookieByName, setCookie, removeCookie } from '@helpers/session'
import { parse } from '@helpers/shopify'
import { COOKIE_EXPIRE_14_DAYS, GEO_COUNTRY_CODE_COOKIE } from '@consts/index'
import { getSelectedCountry } from '@helpers/currency'
import {
  cartCreateMutation,
  cartLinesUpdateMutation,
  cartLinesAddMutation,
  cartAttributesUpdateMutation,
  cartBuyerIdentityUpdateMutation
} from './mutations'
import {
  readCartQuery,
  readCustomerQuery,
  productPresentmentPricesQuery
} from './queries'
import makeRequest, { ShopifyApiResponse } from './makeRequest'
import {
  ShopifyCheckoutError,
  ShopifyCustomerQuery,
  ShopifyCartCreate,
  ShopifyCartLinesAdd,
  ShopifyCartError,
  ShopifyCartQuery,
  ShopifyCartLinesUpdate,
  ShopifyCartAttributesUpdate,
  ShopifyCartBuyerIdentityUpdate,
  ShopifyAvailableCountry,
  ShopifyMoney,
  ShopifyPresentmentPricesQuery
} from './types'
import {
  Cart,
  CartCreateInput,
  CartLinesInput,
  CartLinesUpdateInput,
  CustomAttribute
} from 'types/checkout'
import { Customer } from 'types/customer'

export type LocalProductPrice = {
  productId: number
  price: number
  compareAtPrice: number | null
  currencyCode: string
}

export async function readCart(cartId: string) {
  try {
    const { data, errors } = await makeRequest<ShopifyCartQuery>(
      readCartQuery,
      {
        cartId
      }
    )

    if (errors?.length) {
      throw errors
    }

    const cart = data?.cart

    if (!cart || Object.keys(cart).length === 0) {
      removeCartCookie()
      throw new Error('This cart was previously completed')
    }

    const parsedCart = parse(cart) as Cart

    if (!parsedCart.lines) {
      throw new Error('lines missing from cart')
    }

    return parsedCart
  } catch (e) {
    const error = e as Error
    const errorMessage = getErrorMessage(error)
    trackError(new Error(`readCart failed - ${errorMessage}`), {
      cartId,
      error
    })
    throw error
  }
}

export function getErrorMessage(error: ShopifyCheckoutError[] | Error) {
  return Array.isArray(error) ? error[0]?.message : error.message
}

export async function cartUpdate(lines: CartLinesUpdateInput[] = []) {
  const cartId = getCookieByName(`cart_${process.env.SHOP_LOCALE}`)

  try {
    if (!cartId) {
      throw Error('No cart id')
    }

    const { data } = await makeRequest<ShopifyCartLinesUpdate>(
      cartLinesUpdateMutation,
      {
        lines,
        cartId
      }
    )

    const { cart, userErrors } = parse(data.cartLinesUpdate) as {
      cart: Cart
      userErrors: ShopifyCartError[]
    }

    if (userErrors?.length) {
      throw userErrors
    }

    addCartCookie(cart.id)

    return cart
  } catch (e) {
    const error = e as Error
    const errorMessage = getErrorMessage(error)
    trackError(new Error(`cartUpdate failed - ${errorMessage}`), {
      cartId,
      lines: JSON.stringify(lines),
      error
    })
    throw error
  }
}

export async function cartAttributesUpdate(
  attributes: CustomAttribute | CustomAttribute[]
) {
  const cartId = getCookieByName(`cart_${process.env.SHOP_LOCALE}`)

  try {
    if (!cartId) {
      throw Error('No cart id')
    }

    const { data, errors } = await makeRequest<ShopifyCartAttributesUpdate>(
      cartAttributesUpdateMutation,
      {
        cartId,
        attributes
      }
    )

    if (errors?.length) {
      throw errors
    }

    const { cart, userErrors } = parse(data.cartAttributesUpdate) as {
      cart: Cart
      userErrors: ShopifyCartError[]
    }

    if (userErrors?.length) {
      throw userErrors
    }

    addCartCookie(cart.id)

    return cart
  } catch (e) {
    const error = e as Error
    const errorMessage = getErrorMessage(error)
    trackError(new Error(`cartAttributesUpdate failed - ${errorMessage}`), {
      cartId,
      attributes: JSON.stringify(attributes),
      error
    })
    throw error
  }
}

export async function cartLinesAdd(lines: CartLinesInput[]) {
  const cartId = getCookieByName(`cart_${process.env.SHOP_LOCALE}`)

  if (!cartId) {
    return createCart(lines)
  }

  try {
    const { data } = await makeRequest<ShopifyCartLinesAdd>(
      cartLinesAddMutation,
      {
        lines,
        cartId
      }
    )

    const { cart, userErrors } = parse(data.cartLinesAdd) as {
      cart: Cart
      userErrors: ShopifyCartError[]
    }

    if (userErrors?.length) {
      throw userErrors
    }

    addCartCookie(cart.id)

    return cart
  } catch (e) {
    const error = e as Error
    const errorMessage = getErrorMessage(error)
    trackError(new Error(`cartLinesAdd failed - ${errorMessage}`), {
      cartId,
      lines: JSON.stringify(lines),
      error
    })
    throw error
  }
}

export async function createCart(lines: CartCreateInput[]) {
  const countryCode = getCountryCode()

  try {
    let result = await makeRequest<ShopifyCartCreate>(cartCreateMutation, {
      cartInput: {
        lines,
        buyerIdentity: { countryCode }
      }
    })

    if (hasInvalidCountryCodeError(result)) {
      result = await makeRequest<ShopifyCartCreate>(cartCreateMutation, {
        cartInput: { lines }
      })
    }

    const { cart, userErrors } = parse(result.data.cartCreate) as {
      cart: Cart
      userErrors: ShopifyCartError[]
    }

    if (userErrors?.length) {
      throw userErrors
    }

    addCartCookie(cart.id)

    return cart
  } catch (e) {
    console.error('ERROR CREATE CART: ', e)
    const error = e as Error
    const errorMessage = getErrorMessage(error)
    trackError(new Error(`createCart failed - ${errorMessage}`), {
      lines: JSON.stringify(lines),
      error
    })
    throw error
  }
}

export async function updateCartBuyerIdentity(countryCode?: string) {
  const cartId = getCookieByName(`cart_${process.env.SHOP_LOCALE}`)
  if (!cartId) return

  try {
    const { data, errors } = await makeRequest<ShopifyCartBuyerIdentityUpdate>(
      cartBuyerIdentityUpdateMutation,
      {
        cartId,
        buyerIdentity: { countryCode }
      }
    )

    if (errors?.length) {
      throw errors
    }

    const { cart, userErrors } = parse(data.cartBuyerIdentityUpdate) as {
      cart: Cart
      userErrors: ShopifyCartError[]
    }

    if (userErrors?.length) {
      throw userErrors
    }

    addCartCookie(cart.id)

    return cart
  } catch (e) {
    const error = e as Error
    const errorMessage = getErrorMessage(error)
    trackError(new Error(`updateCartBuyerIdentity failed - ${errorMessage}`), {
      cartId,
      countryCode,
      error
    })
    throw error
  }
}

export async function fetchProductPresentmentPrices(
  productIds: number[],
  country: string
): Promise<LocalProductPrice[]> {
  const ids = productIds.map(id => `gid://shopify/Product/${id}`)

  const { data, errors } = await makeRequest<ShopifyPresentmentPricesQuery>(
    productPresentmentPricesQuery,
    { ids, country }
  )

  if (errors?.length) {
    throw errors
  }

  return (data.nodes || []).flatMap(node => {
    if (!node?.id) return []

    const price = toPrice(node.priceRange?.minVariantPrice)
    const compareAt = toPrice(node.compareAtPriceRange?.minVariantPrice)

    return {
      productId: Number(node.id.split('/').pop()),
      price,
      compareAtPrice: compareAt > price ? compareAt : null,
      currencyCode: node.priceRange?.minVariantPrice?.currencyCode
    }
  })
}

export async function fetchAvailableCountries(): Promise<
  ShopifyAvailableCountry[]
> {
  const response = await fetch('/data/available-countries.json')

  if (!response.ok) {
    throw new Error(`Failed to fetch available countries: ${response.status}`)
  }

  return response.json() as Promise<ShopifyAvailableCountry[]>
}

export async function readCustomer(customerAccessToken: string) {
  try {
    const { data, errors } = await makeRequest<ShopifyCustomerQuery>(
      readCustomerQuery,
      {
        customerAccessToken
      }
    )

    if (errors) throw errors

    return parse(data) as { customer: Customer }
  } catch (e) {
    const error = e as Error
    trackError(new Error('Error at reading the customer'), {
      error,
      customerAccessToken
    })
    return { customer: undefined }
  }
}

function removeCartCookie() {
  removeCookie(`cart_${process.env.SHOP_LOCALE}`)
}

function addCartCookie(cartId: string) {
  setCookie(`cart_${process.env.SHOP_LOCALE}`, cartId, COOKIE_EXPIRE_14_DAYS)
  removeCookie('th_ftv')
}

function getCountryCode(): string | undefined {
  const countryCode =
    getSelectedCountry() || getCookieByName(GEO_COUNTRY_CODE_COOKIE)?.trim()

  // Ensure value is valid 2-character country code - e.g. AU for Australia
  if (countryCode && /^[a-z]{2}$/i.test(countryCode)) {
    return countryCode.toUpperCase()
  }
}

function hasInvalidCountryCodeError(
  result: ShopifyApiResponse<ShopifyCartCreate>
): boolean {
  return Boolean(
    result.errors?.some(error =>
      error.message?.includes('buyerIdentity.countryCode')
    )
  )
}

function toPrice(money: ShopifyMoney | undefined): number {
  return money ? Number(money.amount) : 0
}
